/**
 * Role Constants
 * Defines the 7 standard roles in the system
 */

export const ROLES = {
  SYSTEM_ADMIN: 'SA',
  CUSTOMER_ADMIN: 'CA',
  CENTER_ADMIN: 'CTA',
  CENTER_DIRECTOR: 'CTD',
  TUTOR: 'TU',
  STUDENT: 'ST',
  PARENT: 'PA',
};

/**
 * Role Hierarchy
 * Higher index = more permissions
 * Used for "at least" role checks
 */
export const ROLE_HIERARCHY = [
  ROLES.STUDENT,          // 0 - Lowest
  ROLES.PARENT,           // 1
  ROLES.TUTOR,            // 2
  ROLES.CENTER_DIRECTOR,  // 3
  ROLES.CENTER_ADMIN,     // 4
  ROLES.CUSTOMER_ADMIN,   // 5
  ROLES.SYSTEM_ADMIN,     // 6 - Highest
];

/**
 * Role Display Names
 */
export const ROLE_NAMES = {
  [ROLES.SYSTEM_ADMIN]: 'System Admin',
  [ROLES.CUSTOMER_ADMIN]: 'Customer Admin',
  [ROLES.CENTER_ADMIN]: 'Center Admin',
  [ROLES.CENTER_DIRECTOR]: 'Center Director',
  [ROLES.TUTOR]: 'Tutor',
  [ROLES.STUDENT]: 'Student',
  [ROLES.PARENT]: 'Parent',
};

/**
 * Role Descriptions
 */
export const ROLE_DESCRIPTIONS = {
  [ROLES.SYSTEM_ADMIN]: 'Full system access across all customers and centers',
  [ROLES.CUSTOMER_ADMIN]: 'Full access across all centers for this customer',
  [ROLES.CENTER_ADMIN]: 'Full access to assigned center',
  [ROLES.CENTER_DIRECTOR]: 'Operational access with limited user management',
  [ROLES.TUTOR]: 'Can manage classes, attendance, and notes',
  [ROLES.STUDENT]: 'Can view own classes and attendance',
  [ROLES.PARENT]: 'Can view children\'s information',
};

/**
 * Get role hierarchy level
 * @param {string} role - Role code (e.g., 'SA', 'CA')
 * @returns {number} - Hierarchy level (higher = more permissions)
 */
export const getRoleLevel = (role) => {
  const index = ROLE_HIERARCHY.indexOf(role);
  return index >= 0 ? index : -1;
};

/**
 * Check if a role has at least the same level as required role
 * @param {string} userRole - User's role
 * @param {string} requiredRole - Required role
 * @returns {boolean}
 */
export const hasRoleLevel = (userRole, requiredRole) => {
  const userLevel = getRoleLevel(userRole);
  const requiredLevel = getRoleLevel(requiredRole);
  return userLevel >= requiredLevel;
};

/**
 * Check if a role is one of the specified roles
 * @param {string} userRole - User's role
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean}
 */
export const isOneOfRoles = (userRole, allowedRoles) => {
  return allowedRoles.includes(userRole);
};

/**
 * Permission-based role filtering
 * Determines which roles a user can assign based on their current role and permissions.
 *
 * Role Assignment Rules:
 * - SA (System Admin): Can assign CTA, CTD roles
 * - CTA (Center Admin): Can assign CTD, TU roles
 * - CTD (Center Director): Can assign TU role
 * - TU (Tutor): Cannot assign any roles
 *
 * @param {string} currentUserRole - Current user's role code (e.g., 'SA', 'CTA')
 * @param {Array<Object>} allRoles - All available roles from API
 * @returns {Array<Object>} Filtered roles the user can assign
 */
export const getAssignableRoles = (currentUserRole, allRoles) => {
  if (!currentUserRole || !allRoles) {
    return [];
  }

  // System Admin: Can assign CTA and CTD
  if (currentUserRole === ROLES.SYSTEM_ADMIN) {
    return allRoles.filter(role =>
      role.name === ROLES.CENTER_ADMIN ||
      role.name === ROLES.CENTER_DIRECTOR
    );
  }

  // Center Admin: Can assign CTD and TU
  if (currentUserRole === ROLES.CENTER_ADMIN) {
    return allRoles.filter(role =>
      role.name === ROLES.CENTER_DIRECTOR ||
      role.name === ROLES.TUTOR
    );
  }

  // Center Director: Can assign TU only
  if (currentUserRole === ROLES.CENTER_DIRECTOR) {
    return allRoles.filter(role =>
      role.name === ROLES.TUTOR
    );
  }

  // Tutor and all others: Cannot assign any roles
  return [];
};

/**
 * Check if current user can assign a specific role
 * @param {string} currentUserRole - Current user's role code
 * @param {string} targetRoleCode - Role code to check if can be assigned
 * @returns {boolean}
 */
export const canAssignRole = (currentUserRole, targetRoleCode) => {
  if (!currentUserRole || !targetRoleCode) {
    return false;
  }

  // System Admin can assign CTA, CTD
  if (currentUserRole === ROLES.SYSTEM_ADMIN) {
    return targetRoleCode === ROLES.CENTER_ADMIN ||
           targetRoleCode === ROLES.CENTER_DIRECTOR;
  }

  // Center Admin can assign CTD, TU
  if (currentUserRole === ROLES.CENTER_ADMIN) {
    return targetRoleCode === ROLES.CENTER_DIRECTOR ||
           targetRoleCode === ROLES.TUTOR;
  }

  // Center Director can assign TU
  if (currentUserRole === ROLES.CENTER_DIRECTOR) {
    return targetRoleCode === ROLES.TUTOR;
  }

  return false;
};

/**
 * Check if a role code is Tutor
 * @param {string} roleCode - Role code (e.g., 'TU')
 * @returns {boolean}
 */
export const isTutorRole = (roleCode) => {
  return roleCode === ROLES.TUTOR;
};
